import { Pencil, Trash2 } from "lucide-react";
import { counterText, NOT_RECORDED } from "../lib/counters";
import { formatClockTime12h } from "../lib/clockTime";
// Review round 3, F4 — `toRateText`/`srText` (D102) moved into
// `recordFigures.js`, the one place this decision lives; this file no
// longer carries its own copy.
import { srText, toRateText } from "../lib/recordFigures";

/**
 * Issue #762 — the season record's own match card ("Every match · newest
 * first", `SeasonRecordScreen.jsx`). Moved into its own file for issue #900
 * (D141/#901's sibling): `OneInnings.dc.html` (the one-match record's own
 * drawing, checked at tag `design-baseline-round-5`) draws no edit/delete/
 * reflection affordance at all — the saved-match step promises "you can add
 * one later, from the match" and the one-match record then gives no way
 * back to it. Since the drawing has none, `RecordOneMatchScreen.jsx` reuses
 * this exact component rather than inventing its own card shape — a
 * DEPARTURE from the drawing, recorded here and in both callers' own
 * headers, not a silent addition.
 *
 * No behaviour change for `SeasonRecordScreen.jsx`'s own callers: same
 * markup, same testids (`match-card-{id}`, `edit-card-{id}`, `del-card-{id}`),
 * same props (`m`, `onEdit`, `onDelete`) — moved, not rewritten.
 */
export function MatchRow({ m, onEdit, onDelete }) {
  const star = m.batting?.runs != null && m.batting?.not_out ? "*" : "";
  // B6(4) (PR #880 review round 2) — `SeasonRecord.dc.html`'s own drawn
  // card reads "[DATE] · 2:00 pm · Club one-day · [SEASON]": 12-hour,
  // lowercase am/pm, never this app's shared `HH:MM` (`dateTimeLabel`,
  // used everywhere else a match's time renders). `formatClockTime12h` is
  // additive — `formatClockTime`/`dateTimeLabel` are untouched for every
  // other caller — so this card composes its own line rather than reusing
  // `dateTimeLabel`, mirroring its "no dangling separator when there is no
  // time" rule by hand for the one case it doesn't cover (a 12-hour time).
  const time12h = formatClockTime12h(m.match_time);
  return (
    <div data-testid={`match-card-${m.match_id}`} style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "var(--nw-ink)" }}>{m.opponent}</div>
          <div className="nw-help">
            {m.match_date || NOT_RECORDED}{time12h ? ` · ${time12h}` : ""} · {m.competition || NOT_RECORDED} · {m.season || NOT_RECORDED}
          </div>
          <div className="nw-help">Team: {m.team || NOT_RECORDED}</div>
        </div>
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          <button type="button" aria-label="Edit match" data-testid={`edit-card-${m.match_id}`} onClick={onEdit} style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer" }}>
            <Pencil size={16} style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
          </button>
          <button type="button" aria-label="Delete match" data-testid={`del-card-${m.match_id}`} onClick={onDelete} style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer" }}>
            <Trash2 size={16} style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}>
        <Cell label="Pos" value={counterText(m.batting?.batting_position)} />
        <Cell label="Runs" value={`${counterText(m.batting?.runs)}${star}`} />
        <Cell label="Balls" value={counterText(m.batting?.balls_faced)} />
        <Cell label="4s" value={counterText(m.batting?.fours)} />
        <Cell label="6s" value={counterText(m.batting?.sixes)} />
        {/* B3 (PR #880 review) — D102: strike rate to one decimal, economy
            to two. The stored value is a rate, not pre-formatted text —
            `counterText` alone printed whatever precision the API/#127
            input happened to store, not the drawn convention. */}
        <Cell label="SR" value={srText(m.batting?.strike_rate)} />
        <Cell label="Wkts" value={counterText(m.bowling?.wickets)} />
        <Cell label="Econ" value={toRateText(m.bowling?.economy_rate)} />
        {/* B7 (PR #880 review round 2) — `dismissal_type` and `not_out` are
            independent fields (api/src/routes/matches.ts's own schema: `z.
            string().nullable().optional()` on the one, `z.boolean()…` on
            the other) — nothing forces a not-out innings to also carry the
            literal string "Not out" in `dismissal_type`. Reading only
            `dismissal_type` therefore showed the dash for a real not-out
            innings the moment that field itself was left unset, even
            though `not_out` said plainly that it was one. The drawing's
            own first card (Runs "42*", Dismissal "Not out") draws exactly
            this case. */}
        <Cell label="Dismissal" value={m.batting?.dismissal_type || (m.batting?.not_out ? "Not out" : NOT_RECORDED)} />
      </div>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1, padding: "6px 8px", background: "var(--nw-line-faint)", borderRadius: 8, minWidth: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>{label}</span>
      <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 15, color: "var(--nw-ink)" }}>{value}</span>
    </div>
  );
}
